/* eslint-disable @typescript-eslint/no-explicit-any */
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import { useTranslation } from 'next-i18next'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { hasAnyOnDemandCoverage } from 'src/utils/onDemand'
import ga from 'src/utils/pixels/gtag'
import { MENU_ITEMS } from './constants'

export type MegaMenuOpenVia = 'hover' | 'click'

// Avoid hydration mismatch: on SSG, useRouter().asPath returns the route
// TEMPLATE (e.g. "/[pillar]/flotillas") rather than the real URL, so
// isPillarActive evaluates differently on server vs client. Gate the active
// computation behind a mounted flag so server + first client render agree.
const useIsClientMounted = (): boolean => {
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])
  return mounted
}

type IProps = {
  activeDropdown: string | null
  onHoverEnter: (id: string) => void
  onHoverLeave: () => void
  /** Click / Enter / Space / touch tap on a menu button. */
  onToggle: (id: string) => void
  /** Backdrop click, link navigation. */
  onClose: () => void
}

type MenuItem = (typeof MENU_ITEMS)[number]
type ColumnItem = MenuItem['columns'][number]['items'][number]

const isInternalPath = (href: string | undefined): href is string =>
  typeof href === 'string' && href.startsWith('/')

const matchesPath = (pathname: string, href: string): boolean =>
  pathname === href || pathname.startsWith(`${href}/`)

const collectHrefs = (item: MenuItem): string[] => {
  const hrefs = item.columns.flatMap((col) => col.items.map((sub) => sub.href))
  if (item.image?.href) hrefs.push(item.image.href)
  return hrefs.filter(isInternalPath)
}

const isPillarActive = (item: MenuItem, pathname: string): boolean => {
  if (!pathname || pathname === '/') return false
  return collectHrefs(item).some((href) => matchesPath(pathname, href))
}

/**
 * Resolves a menu item's `gateBy` flag against runtime coverage state.
 * Returns `true` when the link should be rendered, `false` to omit it.
 */
const isItemVisible = (sub: ColumnItem): boolean => {
  const gateBy = (sub as { gateBy?: string }).gateBy
  if (gateBy === 'on-demand-coverage') return hasAnyOnDemandCoverage()
  return true
}

/**
 * Rows that deliberately have no explainer line (the city list). They carry
 * NO `.DESCRIPTION` key in either locale, and `scripts/check-nav-keys.ts`
 * reads the same flag so it keeps demanding the key from every other row.
 */
const hasNoDescription = (sub: ColumnItem): boolean =>
  (sub as { noDescription?: boolean }).noDescription === true

const trackClick = (url: string) =>
  ga.event('nav_link_click', { url, surface: 'desktop_megamenu' })

/**
 * Desktop mega menu — three buttons, one panel each.
 *
 * Opens on hover AND on click / Enter / Space / touch (it was hover-only,
 * which locked out keyboard users and tablets in the desktop layout —
 * 2026-09-09). Escape closes from anywhere (handled by the parent).
 *
 * Panel is editorial, not a SaaS dropdown: white sheet with a hairline
 * bottom edge, two hairline-divided link columns, and a featured block on
 * a cream surface with a real photo and a Fraunces headline. No shadow, no
 * rounded card, no icon-in-a-grey-square rows. The panel only mounts after
 * an interaction, so `next/image` inside it never touches SSR hydration.
 */
const DesktopMegaMenu = ({
  activeDropdown,
  onHoverEnter,
  onHoverLeave,
  onToggle,
  onClose
}: IProps) => {
  const { t } = useTranslation('nav')
  const { asPath } = useRouter()
  // Strip query string + hash so startsWith() matches against the path only.
  const currentPath = asPath.split(/[?#]/)[0]
  const mounted = useIsClientMounted()

  return (
    <div className="hidden h-full items-center gap-1 lg:flex xl:gap-2">
      {MENU_ITEMS.map((item) => {
        const isOpen = activeDropdown === item.id
        const isActive = mounted && isPillarActive(item, currentPath)
        const panelId = `megamenu-${item.id}`
        const label = t(`${item.translationKey as any}.LABEL` as any)

        return (
          // The hover handlers sit on the BUTTON wrapper only, and the panel
          // is its sibling, not its child: the panel's backdrop covers the
          // whole page, so a wrapper that contained it would never fire
          // mouseleave and a hover-opened menu would never close.
          <div key={item.id} className="flex h-full">
            <div
              className="relative flex h-full py-2"
              onMouseEnter={() => onHoverEnter(item.id)}
              onMouseLeave={onHoverLeave}
            >
              <button
                type="button"
                aria-expanded={isOpen}
                aria-haspopup="true"
                aria-controls={panelId}
                aria-current={isActive ? 'page' : undefined}
                onClick={() => onToggle(item.id)}
                className={`relative flex h-full items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:outline-none ${
                  isOpen
                    ? 'bg-cream-300 text-blue-600'
                    : isActive
                      ? 'text-blue-600'
                      : 'text-ink-500 hover:bg-cream-300 hover:text-ink-700'
                }`}
              >
                <span>{label}</span>
                <svg
                  className={`size-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
                <span
                  aria-hidden="true"
                  className={`pointer-events-none absolute inset-x-3 -bottom-px h-[2px] rounded-full bg-blue-500 transition-opacity duration-200 ${
                    isActive && !isOpen ? 'opacity-100' : 'opacity-0'
                  }`}
                />
              </button>
            </div>

            {isOpen && (
              <div
                id={panelId}
                className="fixed top-(--nav-h) left-0 z-40 flex w-screen justify-center"
              >
                {/* Backdrop — dims the page, click closes. `animate-*`
                    classes are keyframes in public/styles/global.css
                    (opacity + transform only; off under reduced motion). */}
                <div
                  className="animate-backdrop-in fixed inset-0 top-(--nav-h) bg-ink-800/10"
                  onClick={onClose}
                />

                {/* Panel */}
                <div
                  className="animate-megamenu-in relative w-full max-w-[1200px] border-b border-ink-100/15 bg-white-500 px-10 pt-8 pb-10 lg:px-12"
                  onMouseEnter={() => onHoverEnter(item.id)}
                  onMouseLeave={onHoverLeave}
                >
                  {/* 4 + 4 + 4: two link columns and the featured block,
                      no dead column between them. */}
                  <div className="grid grid-cols-12 gap-x-10 lg:gap-x-14">
                    {/* Link columns */}
                    {item.columns.map((col) => (
                      <div key={col.translationKey} className="col-span-4">
                        <p className="border-b border-ink-100/15 pb-3 font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase">
                          {t(
                            `${item.translationKey as any}.${col.translationKey as any}.HEADING` as any
                          )}
                        </p>
                        <ul className="divide-y divide-ink-100/10">
                          {col.items.filter(isItemVisible).map((sub) => (
                            <li key={sub.translationKey}>
                              <Link
                                prefetch={false}
                                href={sub.href}
                                onClick={() => {
                                  trackClick(sub.href)
                                  onClose()
                                }}
                                className="group flex flex-col gap-0.5 py-2.5 focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:outline-none"
                              >
                                <span className="text-[15px] font-medium text-ink-700 transition-colors group-hover:text-blue-500">
                                  {t(
                                    `${item.translationKey as any}.${col.translationKey as any}.${sub.translationKey as any}.LABEL` as any
                                  )}
                                </span>
                                {/* A row that carries no fact (the city
                                    rows) declares `noDescription` in
                                    constants.tsx and has NO locale key —
                                    never an empty string, which
                                    `check-nav-keys` cannot tell apart from
                                    a misfiled key. */}
                                {!hasNoDescription(sub) && (
                                  <span className="text-[12.5px] leading-snug text-ink-200">
                                    {t(
                                      `${item.translationKey as any}.${col.translationKey as any}.${sub.translationKey as any}.DESCRIPTION` as any
                                    )}
                                  </span>
                                )}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}

                    {/* Featured block */}
                    {item.image && (
                      <Link
                        prefetch={false}
                        href={item.image.href}
                        onClick={() => {
                          trackClick(item.image.href)
                          onClose()
                        }}
                        className="group col-span-4 flex flex-col self-start overflow-hidden border border-ink-100/15 bg-cream-300 transition-colors hover:border-ink-100/40 focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:outline-none"
                      >
                        {item.image.src && (
                          <div className="relative aspect-[16/9] w-full overflow-hidden">
                            <Image
                              src={item.image.src}
                              alt=""
                              fill
                              sizes="(min-width: 1024px) 480px, 0px"
                              className="object-cover"
                            />
                            <div
                              aria-hidden="true"
                              className="pointer-events-none absolute inset-0 bg-gradient-to-br from-blue-700/40 via-blue-500/20 to-transparent mix-blend-multiply"
                            />
                          </div>
                        )}
                        <div className="flex flex-1 flex-col gap-2 p-6">
                          <span className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase">
                            {label}
                          </span>
                          <span className="font-display text-[24px] leading-tight font-medium text-ink-700">
                            {t(item.image.labelKey as any)}
                          </span>
                          <span className="text-sm leading-relaxed text-ink-200">
                            {t(item.image.descriptionKey as any)}
                          </span>
                          <span className="mt-auto inline-flex items-center gap-1 pt-4 text-sm font-semibold text-blue-500">
                            <span>{t('MENU.DISCOVER_MORE' as any)}</span>
                            <ArrowForwardRoundedIcon className="text-[16px] transition-transform duration-200 group-hover:translate-x-0.5" />
                          </span>
                        </div>
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default DesktopMegaMenu
